import { Box, IconButton, ListItemText, Menu, MenuItem, Skeleton, Typography } from "@mui/material";
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { GetVisitDetailsQuery, useDeleteVisitEventLogEntryMutation } from "~/graphql/__generated__/graphql";
import VisitStatusChip from "./VisitStatusChip";
import { useZonedDateFormat } from "~/providers/ZonedDateFormatProvider";
import { MouseEvent, useMemo, useState } from "react";
import PermissionOrHide from "../common/PermissionOrHide";
import { Permission } from "~/hooks/permissions";
import { gql } from "@apollo/client";
import { orderBy } from "lodash";
import { useOrgConfig } from "~/providers/OrgConfigProvider";
import VisitStatusChangeForm from "./VisitStatusChangeForm";

type Visit = GetVisitDetailsQuery["visitSingle"];
type EventLogItem = NonNullable<Visit>["eventLog"][number];
type VisitResource = NonNullable<Visit>["visitResources"][number];

type Props = {
  events: EventLogItem[];
  loading: boolean;
  visit: NonNullable<Visit>;
};

type EventGroup = {
  key: string;
  title: string;
  events: EventLogItem[];
  resource?: VisitResource;
};

const VisitEventLogTab = ({ events, loading, visit }: Props) => {
  const { timeLabelWithZone, datef } = useZonedDateFormat();
  const { visitTypes } = useOrgConfig();
  const [anchorEl, setAnchorEl] = useState<{ element: HTMLElement | null; eventId: number | null }>({
    element: null,
    eventId: null,
  });

  const [deleteVisitEventLog] = useDeleteVisitEventLogEntryMutation();

  const groups = useMemo<EventGroup[]>(() => {
    const errands = orderBy(
      (visit.visitResources ?? []).filter(resource => resource.errandStatus),
      "startTime",
    );
    const errandIds = new Set(errands.map(resource => resource.id));
    const visitTypeName = visitTypes[visit.visitTypeId || ""]?.name ?? "Visit";

    return [
      {
        key: "visit",
        title: visitTypeName,
        events: events.filter(event => !event.visitResourceId || !errandIds.has(event.visitResourceId)),
      },
      ...errands.map(resource => ({
        key: `errand-${resource.id}`,
        title: resource.resourceRequirement?.errandType?.name ?? "Errand",
        events: events.filter(event => event.visitResourceId === resource.id),
        resource,
      })),
    ];
  }, [events, visit.visitResources, visit.visitTypeId, visitTypes]);

  const handleClick = (event: MouseEvent<HTMLButtonElement>, eventId: number) => {
    setAnchorEl({ element: event.currentTarget, eventId });
  };

  const handleClose = () => {
    setAnchorEl({ element: null, eventId: null });
  };

  const onRemoveEvent = async (eventId: number) => {
    await deleteVisitEventLog({
      variables: { id: eventId },
      refetchQueries: ["GetVisitDetails"],
    });
  };

  const handleConfirmRemove = () => {
    if (anchorEl.eventId !== null) {
      onRemoveEvent(anchorEl.eventId);
    }
    handleClose();
  };

  const hasErrands = groups.length > 1;
  const hasAnyEvents = groups.some(group => group.events.length > 0);

  return (
    <Box sx={{ mt: 2, pl: 2 }}>
      <PermissionOrHide permission={Permission.manageAnyVisit}>
        <VisitStatusChangeForm
          key={`${visit.id}-${visit.eventStatus}`}
          visit={visit}
        />
      </PermissionOrHide>

      {loading && (
        <>
          <Skeleton
            variant="text"
            height={10}
            sx={{ width: "60%", mb: 0.5 }}
          />
          <Skeleton variant="text" height={10} sx={{ width: "80%" }} />
        </>
      )}
      {!loading && !hasAnyEvents && !hasErrands && (
        <Typography variant="body2">No status change events found.</Typography>
      )}
      {!loading && (hasAnyEvents || hasErrands) && groups.map(group => (
        <Box key={group.key} sx={{ mb: 3 }}>
          <Typography variant="subtitle2" component="h3" sx={{ mb: 1 }}>
            {group.title}
          </Typography>
          {group.events.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No status change events found.
            </Typography>
          )}
          {group.events.map((event, index) => {
            const resource = group.resource
              ?? visit.visitResources.find(vr => vr.id === event.visitResourceId);

            return (
              <Box key={event.id} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2" color="text.secondary">
                  {event.autoGenerated ? "System automatically" : `${event.createdByUser?.firstName} ${event.createdByUser?.lastName}`}{' - '}
                </Typography>
                <VisitStatusChip
                  status={event.event}
                  about={event.logEntryType === "visit_resource_status_update" && !resource?.errandStatus
                    ? "visit_resource"
                    : "visit"}
                  subject={resource?.provider}
                  sx={{ fontSize: '11px', height: '20px' }}
                />
                <Typography variant="body2" color="text.secondary">
                  on {datef(event.eventTime, 'MMMM do')} at {timeLabelWithZone(event.eventTime, true, visit.addressTimezone)}
                </Typography>
                {index === 0 && (
                  <PermissionOrHide permission={Permission.manageVisitStatus}>
                    <IconButton
                      size="small"
                      onClick={(e) => handleClick(e, event.id)}
                      aria-label={`Remove latest ${group.title} status`}
                      sx={{ ml: 'auto', color: 'text.secondary' }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </PermissionOrHide>
                )}
              </Box>
            );
          })}
        </Box>
      ))}
      <PermissionOrHide permission={Permission.manageVisitStatus}>
        <Menu
          anchorEl={anchorEl.element}
          open={Boolean(anchorEl.element)}
          onClose={handleClose}
        >
          <MenuItem onClick={handleConfirmRemove}>
            <ListItemText>Confirm Remove</ListItemText>
          </MenuItem>
        </Menu>
      </PermissionOrHide>
    </Box>
  );
};

export default VisitEventLogTab;

gql(`
  mutation DeleteVisitEventLogEntry($id: Int!)
  @permission(requires: manageVisitStatus)
  {
    deleteVisitEventLog(id: $id) {
      id
    }
  }
`);
